import { useEffect, useMemo, useState } from 'react';

import { PredefinedMilisecondsTypeEnum } from '../lib/enums/predefined-miliseconds-type.enum';
import SmallChartDataCache from '../lib/store-util/SmallChartDataCache';
import TradePriceCache, { CalculatedTradePrice } from '../lib/store-util/TradePriceCache';

type Stock = {
  symbol: string;
  [key: string]: any;
};

interface UsePriceAndChartLoadingOptions {
  stocks: Stock[];
  isApiPending?: boolean;
  timeoutMs?: number;
  waitForCharts?: boolean;
}

/**
 * Custom hook to check if data is still loading for a list of stocks.
 * Combines API pending state, price/chart data loading state, and timeout logic.
 *
 * @param options - Configuration object
 * @param options.stocks - Array of stock objects with at least a `symbol` property
 * @param options.isApiPending - Optional boolean indicating if the API call is pending
 * @param options.timeoutMs - Optional timeout in milliseconds (default: 2 seconds)
 * @param options.waitForCharts - Optional boolean to wait for chart data (default: true)
 * @returns boolean - true if data is loading and timeout hasn't been reached, false otherwise
 */
export const usePriceAndChartLoading = ({
  stocks,
  isApiPending = false,
  timeoutMs = PredefinedMilisecondsTypeEnum.twoSeconds,
  waitForCharts = true,
}: UsePriceAndChartLoadingOptions): boolean => {
  const [timeoutReached, setTimeoutReached] = useState(false);
  SmallChartDataCache.useAll(state => state);

  // Create a stable key based on the symbols to detect when stocks change
  const stocksKey = useMemo(
    () => stocks.map(s => s.symbol).join(','),
    [stocks],
  );

  useEffect(() => {
    setTimeoutReached(false);
    const timer = setTimeout(() => setTimeoutReached(true), timeoutMs);
    return () => clearTimeout(timer);
  }, [stocksKey, timeoutMs]);

  if (timeoutReached) {
    return false;
  }

  if (isApiPending) {
    return true;
  }

  if (stocks.length === 0) {
    return false;
  }

  // Check each stock for price and chart readiness
  const isDataLoading = stocks.some(stock => {
    const { symbol } = stock;
    const price = TradePriceCache.get(symbol) as CalculatedTradePrice;
    const hasPrice = price?.currentPrice != null || price?.hasInstrumentSnapshotData;

    if (!waitForCharts) {
      // Only wait for price data
      return !hasPrice;
    }

    // Wait for both price AND chart data
    const hasChart = (!!SmallChartDataCache.get(symbol)?.isReady && !!price?.has1DChartData);
    return !(hasPrice && hasChart);
  });

  return isDataLoading;
};
